import React from 'react';
import api from '../../services/api';
import './addressAutocomplete.css';

// Street input with address suggestions.
//
// Renders the street field itself and reports a whole address up when a
// suggestion is taken, so the parent can fill city/state/zip in one go. Typing is
// never blocked: suggestions are additive, and a failed or empty lookup just
// leaves you with a normal text box — plenty of rural job sites are not in any
// geocoder.
function AddressAutocomplete({
  value,
  onChange,          // (streetText) => void — plain typing
  onSelect,          // ({ street, city, state, zip }) => void — suggestion taken
  placeholder = 'Street address',
  id,
  maxLength = 200,
}) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [highlight, setHighlight] = React.useState(-1);
  const boxRef = React.useRef(null);
  // Guards against a slow early request landing after a later one and replacing
  // newer suggestions with stale ones.
  const seq = React.useRef(0);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const lookup = React.useCallback((text) => {
    const q = (text || '').trim();
    if (q.length < 4) { setSuggestions([]); setOpen(false); return; }

    const mine = ++seq.current;
    setLoading(true);
    api.get('/address/suggest', { params: { q } })
      .then((res) => {
        if (mine !== seq.current) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setSuggestions(list);
        setOpen(list.length > 0);
        setHighlight(-1);
      })
      .catch(() => { if (mine === seq.current) { setSuggestions([]); setOpen(false); } })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, []);

  // Debounced so a lookup fires per pause, not per keystroke — the proxy is rate
  // limited upstream.
  React.useEffect(() => {
    const t = setTimeout(() => lookup(value), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const take = (s) => {
    setOpen(false);
    setSuggestions([]);
    if (onSelect) onSelect(s);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && highlight >= 0) { e.preventDefault(); take(suggestions[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="addr-autocomplete" ref={boxRef}>
      <input
        id={id}
        type="text"
        className="form-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length && setOpen(true)}
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete="off"
      />
      {loading && <span className="addr-spinner" aria-hidden="true" />}

      {open && suggestions.length > 0 && (
        <ul className="addr-results" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={`${s.street}-${s.zip}-${i}`}
              role="option"
              aria-selected={i === highlight}
              className={i === highlight ? 'highlight' : ''}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => { e.preventDefault(); take(s); }}
            >
              <span className="addr-street">{s.street}</span>
              <span className="addr-rest">
                {[s.city, [s.state, s.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
